import { useEffect, useRef, useState } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

// Observers track an unclipped wrapper: a fully clipped element reports zero intersection area.
// Reveals wait for the photo to load so the sweep plays on the real image instead of popping in.
const useImageLoaded = () => {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) setLoaded(true);
  }, []);
  return { loaded, setLoaded, imgRef };
};

export const CurtainImage = ({
  src, alt, className, wrapperClass, direction = "down", desktopSrc, objectPosition,
}: {
  src: string; alt: string; className: string; wrapperClass: string;
  direction?: "down" | "up"; desktopSrc?: string; objectPosition?: string;
}) => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>(0.2);
  const { loaded, setLoaded, imgRef } = useImageLoaded();
  const show = visible && loaded;
  const hiddenClip = direction === "up" ? "inset(100% 0 0 0)" : "inset(0 0 100% 0)";
  const img = (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      loading="eager"
      decoding="async"
      onLoad={() => setLoaded(true)} onError={() => setLoaded(true)}
      className={className}
      style={{
        clipPath: show ? "inset(0 0 0 0)" : hiddenClip,
        transition: "clip-path 1s ease-in-out",
        willChange: "clip-path",
        objectPosition,
      }}
    />
  );
  return (
    <div ref={ref} className={wrapperClass}>
      {desktopSrc ? (
        <picture>
          <source media="(min-width: 1024px)" srcSet={desktopSrc} />
          {img}
        </picture>
      ) : img}
    </div>
  );
};

export const CurtainPanel = ({ src, alt, articleClass, imgClass, children }: { src: string; alt: string; articleClass: string; imgClass: string; children: React.ReactNode }) => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>(0.2);
  const { loaded, setLoaded, imgRef } = useImageLoaded();
  const show = visible && loaded;
  return (
    <div ref={ref}>
      <article
        className={articleClass}
        style={{
          clipPath: show ? "inset(0 0 0 0)" : "inset(100% 0 0 0)",
          transition: "clip-path 1s ease-in-out",
          willChange: "clip-path",
        }}
      >
        <img ref={imgRef} src={src} alt={alt} loading="eager" decoding="async" onLoad={() => setLoaded(true)} onError={() => setLoaded(true)} className={imgClass} />
        {children}
      </article>
    </div>
  );
};

/** Slow lift (whole block) or rise-in (small cards), staggered by index, plays once. */
export const RevealBlock = ({
  index = 0, children, className = "", variant = "lift", as = "div",
}: {
  index?: number; children: React.ReactNode; className?: string; variant?: "lift" | "rise"; as?: "div" | "article";
}) => {
  const { ref, visible } = useScrollReveal<HTMLElement>(0.1);
  const Tag = as as any;
  const anim = variant === "lift" ? "animate-lift-in" : "animate-rise-in";
  return (
    <Tag
      ref={ref}
      className={`${className} ${visible ? anim : "opacity-0"}`}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      {children}
    </Tag>
  );
};
